import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { deliveryAPI, jobsAPI } from '@/services/api';
import DeliveryFileCard from '@/components/user/DeliveryFileCard';
import JobStatusBadge from '@/components/user/JobStatusBadge';
import EmptyState from '@/components/user/EmptyState';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { dedupeDeliveryFiles, needsDeliveryPolling } from '@/utils/deliveryHelpers';
import { toast } from 'sonner';
import { ArrowLeft, Download } from 'lucide-react';

const ClienteDeliveryPage = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [delivery, setDelivery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [revisionFile, setRevisionFile] = useState(null);
  const [revisionMessage, setRevisionMessage] = useState('');
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectMessage, setRejectMessage] = useState('');
  const [downloadLoading, setDownloadLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      const [jobRes, deliveryRes] = await Promise.all([
        jobsAPI.getById(jobId),
        deliveryAPI.getByJob(jobId),
      ]);
      setJob(jobRes.data);
      setDelivery(deliveryRes.data);
    } catch (err) {
      if (err.response?.status !== 404) {
        toast.error('Erro ao carregar entrega.');
      }
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!needsDeliveryPolling(delivery, job)) return undefined;
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [delivery, job, load]);

  const files = useMemo(() => dedupeDeliveryFiles(delivery?.files || []), [delivery]);

  const canApproveDelivery =
    delivery &&
    ['delivered', 'revision_requested', 'approved'].includes(delivery.status) &&
    files.length > 0 &&
    files.every((f) => (f.client_status || 'pending') === 'approved');

  const canDownload =
    job &&
    (job.status === 'completed' || job.status === 'approved' || delivery?.status === 'approved');

  const handleApproveFile = async (file) => {
    if (!delivery?.id || !file.id) return;
    setActionLoading(true);
    try {
      await deliveryAPI.approveFile(delivery.id, file.id, 'approve_file');
      toast.success('Mídia aprovada.');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao aprovar mídia.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRequestRevision = async () => {
    if (!delivery?.id || !revisionFile?.id || !revisionMessage.trim()) {
      toast.error('Informe o motivo da revisão.');
      return;
    }
    setActionLoading(true);
    try {
      await deliveryAPI.approveFile(
        delivery.id,
        revisionFile.id,
        'request_revision_file',
        revisionMessage.trim()
      );
      toast.success('Revisão solicitada.');
      setRevisionFile(null);
      setRevisionMessage('');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao solicitar revisão.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleApproveDelivery = async () => {
    if (!delivery?.id) return;
    setActionLoading(true);
    try {
      await deliveryAPI.approve(delivery.id, { action: 'approve' });
      toast.success('Entrega aprovada.');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao aprovar entrega.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!delivery?.id || !rejectMessage.trim()) {
      toast.error('Informe o motivo da rejeição.');
      return;
    }
    setActionLoading(true);
    try {
      await deliveryAPI.approve(delivery.id, {
        action: 'reject',
        message: rejectMessage.trim(),
      });
      toast.success('Entrega rejeitada.');
      setRejectOpen(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao rejeitar.');
    } finally {
      setActionLoading(false);
    }
  };

  const openDownloads = async () => {
    setDownloadLoading(true);
    try {
      const { data } = await deliveryAPI.getOriginalDownloadLinks(jobId);
      const list = data.files || [];
      if (!list.length) {
        toast.error('Nenhum arquivo disponível para download.');
        return;
      }
      list.forEach((f) => {
        if (f.download_url) window.open(f.download_url, '_blank', 'noopener,noreferrer');
      });
      toast.success(`${list.length} link(s) gerado(s).`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Download indisponível no momento.');
    } finally {
      setDownloadLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-40 bg-white/10" />
        <Skeleton className="h-48 w-full bg-white/10" />
      </div>
    );
  }

  if (!job) {
    return (
      <EmptyState
        title="Job não encontrado"
        actionLabel="Voltar"
        onAction={() => navigate('/cliente/jobs')}
      />
    );
  }

  return (
    <div className="space-y-6">
      <Button variant="ghost" size="sm" onClick={() => navigate('/cliente/jobs')} className="text-white/70">
        <ArrowLeft className="h-4 w-4 mr-1" />
        Voltar
      </Button>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">{job.titulo || 'Entrega'}</h1>
          <JobStatusBadge status={job.status} />
        </div>
        {canDownload && (
          <Button
            onClick={openDownloads}
            disabled={downloadLoading}
            className="bg-[#0E76FF] hover:bg-[#2563EB]"
          >
            <Download className="h-4 w-4 mr-2" />
            Baixar todos
          </Button>
        )}
      </div>

      {!delivery ? (
        <EmptyState
          title="Aguardando entrega"
          description="O videomaker ainda não enviou as mídias deste job."
        />
      ) : (
        <>
          {delivery.message && (
            <p className="text-sm text-white/70 rounded-lg bg-white/5 p-3 border border-white/10">
              {delivery.message}
            </p>
          )}

          <div className="grid gap-4 md:grid-cols-2">
            {files.map((file) => (
              <DeliveryFileCard
                key={file.id || file.filename}
                file={file}
                mode="client"
                loadingAction={actionLoading}
                onApproveFile={handleApproveFile}
                onRequestRevision={(f) => {
                  setRevisionFile(f);
                  setRevisionMessage('');
                }}
                onDownload={canDownload ? () => openDownloads() : undefined}
              />
            ))}
          </div>

          {files.length === 0 && (
            <EmptyState title="Nenhum arquivo na entrega" />
          )}

          <div className="flex flex-wrap gap-3 pt-4 border-t border-white/10">
            {canApproveDelivery && (
              <Button
                onClick={handleApproveDelivery}
                disabled={actionLoading}
                className="bg-[#0E76FF] hover:bg-[#2563EB]"
              >
                Aprovar entrega completa
              </Button>
            )}
            {delivery.status === 'delivered' && (
              <Button variant="destructive" onClick={() => setRejectOpen(true)} disabled={actionLoading}>
                Rejeitar entrega
              </Button>
            )}
          </div>
        </>
      )}

      <Dialog open={!!revisionFile} onOpenChange={(o) => !o && setRevisionFile(null)}>
        <DialogContent className="bg-[#0d1528] border-white/20 text-white">
          <DialogHeader>
            <DialogTitle>Pedir revisão</DialogTitle>
          </DialogHeader>
          <Textarea
            value={revisionMessage}
            onChange={(e) => setRevisionMessage(e.target.value)}
            placeholder="Descreva o que precisa ser ajustado"
            className="bg-white/5 border-white/20 min-h-[100px]"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRevisionFile(null)}>
              Cancelar
            </Button>
            <Button onClick={handleRequestRevision} disabled={actionLoading}>
              Enviar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <AlertDialogContent className="bg-[#0d1528] border-white/20 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Rejeitar entrega?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação exige um motivo. O videomaker será notificado.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Textarea
            value={rejectMessage}
            onChange={(e) => setRejectMessage(e.target.value)}
            className="bg-white/5 border-white/20"
          />
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleReject} className="bg-red-600">
              Confirmar rejeição
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ClienteDeliveryPage;
